/**
 * useEstimateOutreach.ts — Billing Phase 1E
 * Hooks de outreach para estimates. Espejo trimmed de useProjectOutreach.ts
 * (sin schedule/regenerate/skip/followup/manual-draft — fuera de scope de esta fase).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface EstimateOutreachDraft {
  id: string;
  estimateId: number;
  contactId: number;
  sequenceOrder: number;
  draftType: 'estimate_review' | 'reply';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  status: 'pending_review' | 'sent' | 'cancelled' | 'failed';
  cancellationReason: string | null;
  sendError: string | null;
  sendAttemptedAt: number | null;
  inReplyToMessageId: string | null;
  sentAt: number | null;
  sentMessageId: string | null;
  createdAt: number;
  updatedAt: number;
  generatedByClaude: number;
  claudeCostUsd: number | null;
}

export interface EstimateOutreachReply {
  id: string;
  estimateId: number;
  contactId: number;
  fromAddress: string;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  receivedAt: number;
  messageId: string;
  inReplyTo: string | null;
  matchedDraftId: string | null;
  createdAt: number;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useEstimateDrafts(estimateId: number | null) {
  return useQuery<{ drafts: EstimateOutreachDraft[] }>({
    queryKey: ['estimate-outreach-drafts', estimateId],
    queryFn: () => api.get<{ drafts: EstimateOutreachDraft[] }>(`/api/billing/estimates/${estimateId}/outreach/drafts`),
    enabled: estimateId !== null,
  });
}

export function useEstimateReplies(estimateId: number | null) {
  return useQuery<{ replies: EstimateOutreachReply[] }>({
    queryKey: ['estimate-outreach-replies', estimateId],
    queryFn: () => api.get<{ replies: EstimateOutreachReply[] }>(`/api/billing/estimates/${estimateId}/outreach/replies`),
    enabled: estimateId !== null,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useComposeEstimateSend(estimateId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }>(
        `/api/billing/estimates/${estimateId}/outreach/compose-send`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimate-outreach-drafts', estimateId] });
    },
  });
}

export function useComposeEstimateReply(estimateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ replyId, bodyHtml, bodyText }: { replyId: string; bodyHtml: string; bodyText: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string }>(
        `/api/billing/estimates/${estimateId}/outreach/replies/${replyId}/compose-reply`,
        { bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimate-outreach-drafts', estimateId] });
    },
  });
}

export function useUpdateEstimateDraft(estimateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, subject, bodyHtml, bodyText }: { draftId: string; subject?: string; bodyHtml?: string; bodyText?: string }) =>
      api.patch<EstimateOutreachDraft>(
        `/api/billing/estimates/${estimateId}/outreach/drafts/${draftId}`,
        { subject, bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimate-outreach-drafts', estimateId] });
    },
  });
}

export function useSendEstimateDraft(estimateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      api.post<{ ok: boolean; messageId: string }>(`/api/billing/estimates/${estimateId}/outreach/drafts/${draftId}/send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimate-outreach-drafts', estimateId] });
      // Sending flips estimate.status server-side — refresh the estimate itself too.
      void qc.invalidateQueries({ queryKey: ['billing-estimate', estimateId] });
      void qc.invalidateQueries({ queryKey: ['billing-estimates'] });
    },
  });
}

export function useCancelEstimateDraft(estimateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, reason }: { draftId: string; reason?: string }) =>
      api.post<{ ok: boolean }>(`/api/billing/estimates/${estimateId}/outreach/drafts/${draftId}/cancel`, reason ? { reason } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimate-outreach-drafts', estimateId] });
    },
  });
}
